// components/pages/SponsorSubmit.js
// Public sponsorship submission form. On success we tell the sponsor to check their email
// for a portal link (they also get one on screen). Mirrors the Contact form conventions.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Container from '../common/Container';
import { SectionTitle, SectionSubtitle } from '../common/SharedStyles';
import Button from '../common/Button';
import { submitSponsorship } from '../../services/sponsorshipService';

const Section = styled.section`
  padding: 6rem 0;
  background-color: var(--background-primary);
`;

const NarrowContainer = styled(Container)`
  max-width: 640px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--background-primary);
  color: var(--text-primary);

  &:focus { outline: none; border-color: var(--accent-primary); }
`;

const LogoPreview = styled.img`
  max-width: 180px;
  max-height: 90px;
  object-fit: contain;
  margin-top: 0.5rem;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 6px;
`;

const FormStatus = styled.div`
  margin-top: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
  font-size: 0.9rem;

  &.error { background-color: rgba(229, 57, 53, 0.12); color: #e53935; }
  &.success { background-color: rgba(67, 160, 71, 0.12); color: #43a047; }
  &.sending { background-color: rgba(3, 169, 244, 0.12); color: #0288d1; }
`;

const Hint = styled.p`
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin: 0;
`;

const PortalLink = styled.a`
  word-break: break-all;
  color: var(--accent-primary);
`;

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const SponsorSubmit = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [formData, setFormData] = useState({ companyName: '', contactName: '', contactEmail: '', destinationUrl: '' });
  const [logoData, setLogoData] = useState('');
  const [status, setStatus] = useState('');
  const [statusClass, setStatusClass] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [portalLink, setPortalLink] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogo = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setLogoData(''); return; }
    if (file.size > 2 * 1024 * 1024) {
      setStatus('Logo is too large (max 2 MB).');
      setStatusClass('error');
      e.target.value = '';
      return;
    }
    try {
      const dataUrl = await readFileAsDataURL(file);
      setLogoData(dataUrl);
      setStatus('');
      setStatusClass('');
    } catch (err) {
      setStatus('Could not read that image.');
      setStatusClass('error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.contactName || !formData.contactEmail || !formData.destinationUrl) {
      setStatus('Please fill in all fields.');
      setStatusClass('error');
      return;
    }
    if (!logoData) {
      setStatus('Please upload your logo.');
      setStatusClass('error');
      return;
    }

    setIsSending(true);
    setStatus('Submitting your sponsorship...');
    setStatusClass('sending');

    try {
      const result = await submitSponsorship({ ...formData, logoData });
      setStatus('Submission received! Check your email for your portal link. Our team will review it shortly.');
      setStatusClass('success');
      setPortalLink(result.portalLink || '');
      setFormData({ companyName: '', contactName: '', contactEmail: '', destinationUrl: '' });
      setLogoData('');
    } catch (error) {
      setStatus(`Error: ${error.message}`);
      setStatusClass('error');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Section id="sponsor-submit">
      <NarrowContainer>
        <SectionTitle>Sponsor Sivo</SectionTitle>
        <SectionSubtitle>
          Get your company's logo in front of Sivo users. Submit your details below — once approved,
          you can start a $100 / 30-day sponsorship and your logo goes live in the app sidebar.
        </SectionSubtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <label>Company Name</label>
            <Input type="text" name="companyName" placeholder="Acme Inc." value={formData.companyName} onChange={handleChange} />
          </FormGroup>
          <FormGroup>
            <label>Contact Name</label>
            <Input type="text" name="contactName" placeholder="Jane Doe" value={formData.contactName} onChange={handleChange} />
          </FormGroup>
          <FormGroup>
            <label>Contact Email</label>
            <Input type="email" name="contactEmail" placeholder="jane@acme.com" value={formData.contactEmail} onChange={handleChange} />
          </FormGroup>
          <FormGroup>
            <label>Destination Website URL</label>
            <Input type="url" name="destinationUrl" placeholder="https://acme.com" value={formData.destinationUrl} onChange={handleChange} />
            <Hint>Where users go when they click your logo.</Hint>
          </FormGroup>
          <FormGroup>
            <label>Logo</label>
            <Input type="file" accept="image/*" onChange={handleLogo} />
            <Hint>PNG, JPG, SVG, GIF or WebP. Max 2 MB.</Hint>
            {logoData && <LogoPreview src={logoData} alt="Logo preview" />}
          </FormGroup>

          {status && <FormStatus className={statusClass}>{status}</FormStatus>}
          {portalLink && (
            <Hint>
              Your portal link: <PortalLink href={portalLink}>{portalLink}</PortalLink>
            </Hint>
          )}

          <Button variant="primary" type="submit" disabled={isSending}>
            {isSending ? 'Submitting...' : 'Submit Sponsorship'}
          </Button>
          <Hint>
            Already submitted? Use the portal link we emailed you, or <Link to="/">return home</Link>.
          </Hint>
        </Form>
      </NarrowContainer>
    </Section>
  );
};

export default SponsorSubmit;
